// frontend/src/components/ESIMPackages/ESIMPackages.js
import React, { useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import FilterPanel from "./FilterPanel";
import ESIMPackageTable from "./ESIMPackageTable";
import PaginationControl from "./PaginationControl";
import PurchaseModal from "./PurchaseModal";

const ESIMPackages = ({
  packages = [],
  loading,
  purchaseLoading,
  onRefresh,
  onPurchase,
  setLoading,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [filters, setFilters] = useState({
    name: "",
    region: "",
    duration: "",
    data: "",
    unit: "GB",
  });
  const [purchaseModal, setPurchaseModal] = useState({
    open: false,
    selectedPackage: null,
  });

  // Helper function to convert data size to bytes based on unit
  const convertToBytes = (value, unit) => {
    if (!value) return 0;
    const size = parseFloat(value);
    if (isNaN(size)) return 0;
    return unit === "GB" ? size * 1024 * 1024 * 1024 : size * 1024 * 1024;
  };

  // Enhanced filtering function
  const filteredPackages = packages.filter((pkg) => {
    // Name filter
    if (
      filters.name &&
      !pkg.name.toLowerCase().includes(filters.name.toLowerCase())
    ) {
      return false;
    }

    // Region filter
    if (
      filters.region &&
      !pkg.location?.toLowerCase().includes(filters.region.toLowerCase()) &&
      !pkg.locationNetworkList?.some((loc) =>
        loc.locationName?.toLowerCase().includes(filters.region.toLowerCase())
      )
    ) {
      return false;
    }

    // Duration filter
    if (filters.duration && pkg.duration !== parseInt(filters.duration)) {
      return false;
    }

    // Data filter
    if (filters.data) {
      const packageBytes = pkg.volume; // Package volume in bytes
      const filterBytes = convertToBytes(filters.data, filters.unit);
      if (packageBytes < filterBytes) {
        return false;
      }
    }

    return true;
  });

  const handlePurchaseClick = (pkg) => {
    setPurchaseModal({ open: true, selectedPackage: pkg });
  };

  const handlePurchaseComplete = async (purchaseData) => {
    const success = await onPurchase(purchaseData);
    if (success) {
      setPurchaseModal({ open: false, selectedPackage: null });
      onRefresh();
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters({ ...filters, [field]: event.target.value });
    setPage(0); // Reset to first page when filters change
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sorting function
  const sortedPackages = filteredPackages.sort((a, b) => {
    const isAsc = order === "asc";
    switch (orderBy) {
      case "name":
        return isAsc
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      case "price":
        return isAsc ? a.price - b.price : b.price - a.price;
      case "data":
        return isAsc ? a.volume - b.volume : b.volume - a.volume;
      case "duration":
        return isAsc ? a.duration - b.duration : b.duration - a.duration;
      default:
        return 0;
    }
  });

  const paginatedPackages = sortedPackages.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Extract unique regions from packages for the region filter dropdown
  const regions = [
    ...new Set(
      packages
        .map((pkg) => [
          pkg.location,
          ...(pkg.locationNetworkList?.map((loc) => loc.locationName) || []),
        ])
        .flat()
        .filter(Boolean)
    ),
  ].sort();

  return (
    <Box sx={{ width: "100%" }}>
      <FilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        regions={regions}
        filterLabels={{
          name: t("esim.packages.filters.name"),
          region: t("esim.packages.filters.region"),
          duration: t("esim.packages.filters.duration"),
          data: t("esim.packages.filters.data"),
          unit: t("esim.packages.filters.unit"),
        }}
      />
      <ESIMPackageTable
        packages={paginatedPackages} // Pass the paginated packages here
        orderBy={orderBy}
        order={order}
        onSort={handleSort}
        onPurchase={handlePurchaseClick}
        loading={loading}
      />
      <PaginationControl
        count={filteredPackages.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <PurchaseModal
        open={purchaseModal.open}
        onClose={() => setPurchaseModal({ open: false, selectedPackage: null })}
        onPurchase={handlePurchaseComplete}
        loading={purchaseLoading}
        selectedPackage={purchaseModal.selectedPackage}
        setLoading={setLoading}
      />
    </Box>
  );
};

export default ESIMPackages;
